/**
 * highlightMarkers — Walter 2026-06-20. Manual line-level "priority" highlights.
 *
 * A highlight is a tiny LEADING marker on a line, glued to the first non-space
 * character: `^` green, `^^` blue, `^^^` red. Because it lives in the text, it
 * rides the existing content sync/merge for free (no parallel store to drift
 * out of lockstep) and the SyntaxBackdrop washes the whole line behind the
 * (transparent) textarea — same mechanism as the header/reminder tints.
 *
 * CONTAINED v1 (Walter "build it contained so we can roll back"): highlighting
 * applies ONLY to plain lines. Header (`#`) and reminder/bell lines — and every
 * line in a designated reminder/log list — are SKIPPED, because a leading
 * marker would change a line's first character and the reminder-capture engine
 * keys off the leading `🔔` (BELL_RE). Touching that engine is out of scope for
 * this batch; we can extend to those line types later with proper parser work.
 *
 * Everything here is pure (no React) so it's easy to test and reason about,
 * mirroring lib/moveLines.js.
 */

// Master switch — flip to false to fully disable manual highlights (keyboard
// handler, popup, AND the backdrop's marker rendering) without touching the
// approved auto-highlight (headers/reminders). Roll-back in one line.
export const MANUAL_HIGHLIGHTS_ENABLED = true;

// colorIndex → leading marker. 1 green, 2 blue, 3 red. 0 = remove.
export const HL_MARKERS = { 1: '^', 2: '^^', 3: '^^^' };

// Display metadata for the popup. `cssVar` resolves per-theme in index.css.
export const HL_COLORS = [
  { idx: 1, key: 'green', label: 'green', cssVar: '--hl-green' },
  { idx: 2, key: 'blue', label: 'blue', cssVar: '--hl-blue' },
  { idx: 3, key: 'red', label: 'red', cssVar: '--hl-red' },
];

// Leading 1–3 `^` highlight marker. Walter 2026-06-23: a space after the
// caret(s) still counts ("^ buy milk"). Walter 2026-06-25: dropped the
// "must have content after" requirement so a BARE `^` on an otherwise-empty
// line is a valid (empty) highlight — the Format buttons now work on empty
// lines, and an empty highlight must still toggle/show-active correctly.
const HL_MARKER_RE = /^(\^{1,3})/;

// Header lines (#) and bell/[DONE] lines are owned by other systems — skip.
const HEADER_RE = /^\s{0,3}#{1,6}\s/;
const BELL_RE = /^(?:🔔|\[DONE\]|🔕|~~🔔)/;

// "Mark as done" — Walter 2026-06-23. Ctrl+Shift+H greys out a line via a
// leading `[x]` marker (visible, glued to the front like `^`, so it rides
// content sync for free) that the SyntaxBackdrop reads to lay a translucent
// veil OVER the line. Walter wanted the marker visible at the start "for
// consistency" with `^`, and no space after it (2026-06-23) — so `[x]groceries`,
// glued like `^groceries`. `[DONE]` already means "a fired reminder" (BELL_RE),
// so a distinct `[x]` keeps the two from colliding. The RE still tolerates one
// optional space so older `[x] ` lines un-mark cleanly. Done and colour-
// highlight are mutually exclusive on a line (marking done strips any `^`; a
// done line can't be colour-highlighted until un-done).
export const DONE_MARKER = '[x]';
const DONE_RE = /^\[x\]\s?/i;

// Does this line carry the done marker?
export function isDoneLine(line) {
  return DONE_RE.test(line || '');
}

export function cssVarForColor(colorIndex) {
  const c = HL_COLORS.find((x) => x.idx === colorIndex);
  return c ? `var(${c.cssVar})` : null;
}

// Parse a line's leading highlight marker → { colorIndex, markerLen } | null.
/**
 * PAIRED highlight spans — Walter 2026-08-22. `^word^` highlights just that
 * word, exactly the way `**word**` bolds just that word; his framing was
 * "we're pretty much mirroring bold, italics, and strikethrough here". The
 * marker count picks the colour as always: ^ green, ^^ blue, ^^^ red, and the
 * closing run must match the opening one.
 *
 * The whole-line highlight is now simply the UNCLOSED case — `^buy milk` with
 * nothing closing it still washes the line, so everything he has already typed
 * keeps rendering as before. "Close it to limit it."
 *
 * Content may not itself contain `^`, same restriction the emphasis patterns
 * use, which is what makes an unpaired caret inert and visible rather than
 * swallowing the rest of the line.
 */
export const HL_SPAN_RE = /(\^{1,3})([^^\n]+?)\1/g;

/** All paired spans on a line: [{ start, end, colorIndex, markerLen }]. */
export function parseHighlightSpans(line) {
  const out = [];
  const text = line || '';
  const re = new RegExp(HL_SPAN_RE.source, 'g');
  let m;
  while ((m = re.exec(text))) {
    out.push({
      start: m.index,
      end: m.index + m[0].length,
      colorIndex: m[1].length,
      markerLen: m[1].length,
    });
  }
  return out;
}

/** Is the line's LEADING marker actually the opening half of a pair? */
export function startsWithHighlightSpan(line) {
  const spans = parseHighlightSpans(line);
  return spans.length > 0 && spans[0].start === 0;
}

export function parseHighlightMarker(line) {
  const m = HL_MARKER_RE.exec(line || '');
  if (!m) return null;
  // A leading marker that CLOSES later is a span, not a whole-line highlight
  // (Walter 2026-08-22: "'^important^ and the rest' really only highlights
  // that one word").
  if (startsWithHighlightSpan(line)) return null;
  return { colorIndex: m[1].length, markerLen: m[1].length };
}

// Is this line eligible for a manual highlight? (Plain lines only in v1.)
export function isHighlightableLine(line, opts = {}) {
  if (opts.isReminderList) return false; // every line is a reminder there
  const t = line || '';
  if (!t.trim()) return false; // blank
  if (isDoneLine(t)) return false; // done & colour-highlight are exclusive
  // A line that already carries a highlight marker is still highlightable
  // (re-color / remove); strip it before checking the underlying semantics.
  const stripped = t.replace(HL_MARKER_RE, '');
  if (HEADER_RE.test(stripped)) return false;
  if (BELL_RE.test(stripped)) return false;
  return true;
}

// Is this line eligible for "mark as done"? Same plain-lines-only rule as
// highlights, but an already-done line stays eligible (so you can un-done it).
export function isDoneEligible(line, opts = {}) {
  if (opts.isReminderList) return false;
  const t = line || '';
  if (!t.trim()) return false;
  // Strip both possible leading markers before judging the underlying line.
  const stripped = t.replace(DONE_RE, '').replace(HL_MARKER_RE, '');
  if (!stripped.trim()) return false; // marker(s) only, no real content
  if (HEADER_RE.test(stripped)) return false;
  if (BELL_RE.test(stripped)) return false;
  return true;
}

/**
 * Toggle the "done" marker on every eligible line in the block spanned by
 * [selStart, selEnd]. `on` true → add `[x] ` (and strip any colour `^`);
 * `on` false → remove `[x] `. Mirrors setLinesHighlight's return shape.
 *
 * @returns {{ nextValue, nextSelStart, nextSelEnd, changed, skipped }}
 */
export function setLineDone(content, selStart, selEnd, on, opts = {}) {
  const value = content || '';
  const s = Math.max(0, Math.min(selStart, value.length));
  const e = Math.max(s, Math.min(selEnd, value.length));

  const blockStart = value.lastIndexOf('\n', s - 1) + 1;
  let blockEnd = value.indexOf('\n', e);
  if (e > s && value[e - 1] === '\n') blockEnd = e - 1;
  if (blockEnd === -1) blockEnd = value.length;

  const block = value.slice(blockStart, blockEnd);
  const lines = block.split('\n');

  let anyEligible = false;
  let firstLineDelta = 0;
  const newLines = lines.map((line, i) => {
    if (!isDoneEligible(line, opts)) return line;
    anyEligible = true;
    let body = line.replace(DONE_RE, ''); // drop any existing done marker
    let next;
    if (on) {
      body = body.replace(HL_MARKER_RE, ''); // done supersedes a colour marker
      next = DONE_MARKER + body;
    } else {
      next = body; // un-done; do NOT restore a previously-stripped colour
    }
    if (i === 0) firstLineDelta = next.length - line.length;
    return next;
  });

  const newBlock = newLines.join('\n');
  const nextValue = value.slice(0, blockStart) + newBlock + value.slice(blockEnd);
  const changed = newBlock !== block;

  let nextSelStart;
  let nextSelEnd;
  if (s === e) {
    const caret = Math.max(blockStart, s + (s >= blockStart ? firstLineDelta : 0));
    nextSelStart = caret;
    nextSelEnd = caret;
  } else {
    nextSelStart = blockStart;
    nextSelEnd = blockStart + newBlock.length;
  }

  return { nextValue, nextSelStart, nextSelEnd, changed, skipped: !anyEligible };
}

// ── Format-menu line actions (Walter 2026-06-24) ────────────────────────────
// Header levels, reminder-bell insert, and clear-formatting — the new clickable
// actions behind the header Format ▾ menu. They share the same block/caret
// shape as setLinesHighlight/setLineDone so the editor commit path is identical.

const REMINDER_MARKER = '🔔 ';
const HEADER_HASHES_RE = /^\s*#{1,6}[ \t]+/; // leading hashes + their trailing space

// Strip every leading FORMAT marker from a line body: header hashes, the
// highlight `^`(s), and the done `[x]`. (A 🔔 bell is intentionally NOT stripped
// here — removing it deletes a reminder, which stays an explicit edit.)
function stripFormatMarkers(line) {
  return (line || '')
    .replace(DONE_RE, '')
    .replace(HL_MARKER_RE, '')
    .replace(HEADER_HASHES_RE, '');
}

// Shared block transformer: apply `mapLine(line) → {line, eligible} | null` to
// every line in the [selStart,selEnd] block, tracking caret shift. Mirrors the
// math in setLinesHighlight/setLineDone.
function transformBlock(content, selStart, selEnd, mapLine) {
  const value = content || '';
  const s = Math.max(0, Math.min(selStart, value.length));
  const e = Math.max(s, Math.min(selEnd, value.length));
  const blockStart = value.lastIndexOf('\n', s - 1) + 1;
  let blockEnd = value.indexOf('\n', e);
  if (e > s && value[e - 1] === '\n') blockEnd = e - 1;
  if (blockEnd === -1) blockEnd = value.length;

  const block = value.slice(blockStart, blockEnd);
  const lines = block.split('\n');
  let anyEligible = false;
  let firstLineDelta = 0;
  const newLines = lines.map((line, i) => {
    const res = mapLine(line);
    if (!res || res.eligible === false) return line;
    anyEligible = true;
    if (i === 0) firstLineDelta = res.line.length - line.length;
    return res.line;
  });

  const newBlock = newLines.join('\n');
  const nextValue = value.slice(0, blockStart) + newBlock + value.slice(blockEnd);
  const changed = newBlock !== block;
  let nextSelStart;
  let nextSelEnd;
  if (s === e) {
    const caret = Math.max(blockStart, s + (s >= blockStart ? firstLineDelta : 0));
    nextSelStart = caret;
    nextSelEnd = caret;
  } else {
    nextSelStart = blockStart;
    nextSelEnd = blockStart + newBlock.length;
  }
  return { nextValue, nextSelStart, nextSelEnd, changed, skipped: !anyEligible };
}

// Set the header level (1/2/3) on the caret's line(s). level 0 removes it.
// Replaces any existing header hashes; strips highlight/done first (a header
// line carries neither). Skips blank, bell, and reminder-list lines.
export function setLineHeader(content, selStart, selEnd, level, opts = {}) {
  return transformBlock(content, selStart, selEnd, (line) => {
    if (opts.isReminderList) return null;
    const t = line || '';
    if (!t.trim()) return null;
    if (BELL_RE.test(t)) return null;
    const body = stripFormatMarkers(t);
    if (!body.trim()) return null;
    const marker = level ? `${'#'.repeat(level)} ` : '';
    return { line: marker + body, eligible: true };
  });
}

// Insert a reminder bell on the caret's line(s): prepend "🔔 ". Skips lines that
// are already belled or are headers; strips highlight/done first. An empty line
// becomes a bare "🔔 " for the user to fill — no reminder doc is created until
// they type (see the empty-bell guard in useReminderCapture).
export function insertReminderMarker(content, selStart, selEnd) {
  return transformBlock(content, selStart, selEnd, (line) => {
    const t = line || '';
    if (BELL_RE.test(t)) return null;
    const unmarked = t.replace(HL_MARKER_RE, '').replace(DONE_RE, '');
    if (HEADER_RE.test(unmarked)) return null;
    return { line: REMINDER_MARKER + stripFormatMarkers(t), eligible: true };
  });
}

// Remove a reminder bell from the caret's line(s) — the toggle-off for the
// Format reminder button. Stripping the 🔔 makes the line a plain line again;
// the editor's capture engine (notified on the content change) deletes the
// matching reminder doc. Mirrors useReminderCapture's bell regex.
const BELL_STRIP_RE = /^🔔↻?\s?/;
export function removeReminderMarker(content, selStart, selEnd) {
  return transformBlock(content, selStart, selEnd, (line) => {
    if (!BELL_STRIP_RE.test(line || '')) return null;
    return { line: (line || '').replace(BELL_STRIP_RE, ''), eligible: true };
  });
}

// Clear leading format markers (header hashes, highlight ^, done [x]) from the
// caret's line(s). Leaves a 🔔 bell ALONE. No-op on lines with nothing to clear.
export function clearLineFormat(content, selStart, selEnd) {
  return transformBlock(content, selStart, selEnd, (line) => {
    const t = line || '';
    const body = stripFormatMarkers(t);
    if (body === t) return null;
    return { line: body, eligible: true };
  });
}

// A leading log date stamp — "@YYYY-MM-DD " (Walter 2026-06-24: the Format log
// button prepends today's date, same convention as designated log lists).
const LOG_DATE_STRIP_RE = /^@\d{4}-\d{2}-\d{2}\s?/;

// GFM checkbox (Walter 2026-07-16, format-menu refresh): `- [ ] ` / `- [x] `
// (or `*` bullet). A peer of the other leading markers so the Format menu's
// Checkbox button is exclusive+toggling like the rest. The CM6 editor renders
// it as a clickable box; the textarea shows it raw (same deal as `#`/`^`).
export const CHECKBOX_MARKER = '- [ ] ';
const CHECKBOX_STRIP_RE = /^\s*[-*]\s+\[( |x|X)\]\s?/;

// Does this line carry a GFM checkbox (either state)? → { checked } | null.
export function parseCheckbox(line) {
  const m = CHECKBOX_STRIP_RE.exec(line || '');
  if (!m) return null;
  return { checked: /x/i.test(m[1]) };
}

// Strip EVERY leading format marker: log @date, header #, highlight ^, done [x],
// GFM checkbox `- [ ]`, bell 🔔.
function stripAllMarkers(line) {
  return (line || '')
    .replace(LOG_DATE_STRIP_RE, '')
    .replace(BELL_STRIP_RE, '')
    .replace(DONE_RE, '')
    .replace(HL_MARKER_RE, '')
    // after HL so a highlighted checkbox (`^- [ ] milk`) strips fully
    .replace(CHECKBOX_STRIP_RE, '')
    .replace(HEADER_HASHES_RE, '');
}

// Set the caret line(s) to a SINGLE exclusive format, replacing whatever format
// it currently has — so a Format button is never a no-op (Walter 2026-06-24:
// "every button should always do something… switch from a highlight to a
// header" etc). `marker`: '^'/'^^'/'^^^' | '# '/'## '/'### ' | '[x]' | '🔔 ' |
// '' (plain). Skips designated reminder-list lines (special mode); a blank line
// accepts only the bell (the fillable empty reminder).
export function setLineFormat(content, selStart, selEnd, marker, opts = {}) {
  return transformBlock(content, selStart, selEnd, (line) => {
    if (opts.isReminderList) return null;
    // Walter 2026-06-25: no empty-line restriction — you can format a line
    // before you've typed it. (The `next === t` guard below still no-ops a
    // truly redundant change, e.g. clearing an already-plain blank line.)
    const t = line || '';
    const next = marker + stripAllMarkers(t);
    if (next === t) return null;
    return { line: next, eligible: true };
  });
}

/**
 * Apply (or remove, colorIndex 0) a highlight to every eligible line in the
 * block spanned by [selStart, selEnd]. Returns the new value + adjusted
 * selection, mirroring lib/moveLines.js's shape.
 *
 * @returns {{ nextValue, nextSelStart, nextSelEnd, changed, appliedColor, skipped }}
 *   appliedColor — the color now on the lines (null if removed / none).
 *   skipped — true when NO line in the block was eligible (all header/reminder).
 */
export function setLinesHighlight(content, selStart, selEnd, colorIndex, opts = {}) {
  const value = content || '';
  const s = Math.max(0, Math.min(selStart, value.length));
  const e = Math.max(s, Math.min(selEnd, value.length));

  const blockStart = value.lastIndexOf('\n', s - 1) + 1;
  let blockEnd = value.indexOf('\n', e);
  // If the selection ends exactly at a line break, don't pull in the next line.
  if (e > s && value[e - 1] === '\n') blockEnd = e - 1;
  if (blockEnd === -1) blockEnd = value.length;

  const block = value.slice(blockStart, blockEnd);
  const lines = block.split('\n');

  let anyEligible = false;
  let firstLineDelta = 0; // length change applied to the FIRST line (for caret)
  const newLines = lines.map((line, i) => {
    if (!isHighlightableLine(line, opts)) return line;
    anyEligible = true;
    const existing = parseHighlightMarker(line);
    const oldLen = existing ? existing.markerLen : 0;
    const body = existing ? line.slice(oldLen) : line;
    const marker = colorIndex ? HL_MARKERS[colorIndex] : '';
    if (i === 0) firstLineDelta = marker.length - oldLen;
    return marker + body;
  });

  const newBlock = newLines.join('\n');
  const nextValue = value.slice(0, blockStart) + newBlock + value.slice(blockEnd);
  const changed = newBlock !== block;

  let nextSelStart;
  let nextSelEnd;
  if (s === e) {
    // Cursor only — keep it where it was, shifted by the first line's marker
    // delta (the marker is inserted at/​before the caret).
    const caret = s + (s >= blockStart ? firstLineDelta : 0);
    nextSelStart = caret;
    nextSelEnd = caret;
  } else {
    // Selection — re-select the (re-marked) block so it's clear what changed.
    nextSelStart = blockStart;
    nextSelEnd = blockStart + newBlock.length;
  }

  return {
    nextValue,
    nextSelStart,
    nextSelEnd,
    changed,
    appliedColor: colorIndex && anyEligible ? colorIndex : null,
    skipped: !anyEligible,
  };
}

/**
 * wrapSelectionHighlight — put paired markers around the SELECTED text.
 * Walter 2026-08-22: with a selection the colour buttons should wrap it, the
 * way bold does; with no selection they keep marking the whole paragraph.
 *
 * Single-line selections only — the caller falls back to the per-line
 * behaviour for a multi-line selection, since a span that straddles a line
 * break has no sensible rendering.
 *
 * Toggling off: if the selection is already exactly a span of this colour,
 * the markers are removed instead.
 */
export function wrapSelectionHighlight(content, selStart, selEnd, colorIndex) {
  const value = content || '';
  const s = Math.max(0, Math.min(selStart, value.length));
  const e = Math.max(s, Math.min(selEnd, value.length));
  if (s === e) return null;                        // no selection — caller handles
  if (value.slice(s, e).includes('\n')) return null; // multi-line — caller handles
  const marker = HL_MARKERS[colorIndex];
  if (!marker) return null;

  const selected = value.slice(s, e);
  // Already wrapped in this exact colour? Unwrap.
  const already = selected.startsWith(marker) && selected.endsWith(marker)
    && selected.length >= marker.length * 2;
  const inner = already ? selected.slice(marker.length, selected.length - marker.length) : selected;
  if (!already && inner.includes('^')) return null; // can't nest carets — leave alone

  const replacement = already ? inner : `${marker}${inner}${marker}`;
  return {
    nextValue: value.slice(0, s) + replacement + value.slice(e),
    nextSelStart: s,
    nextSelEnd: s + replacement.length,
    changed: true,
    appliedColor: already ? null : colorIndex,
    skipped: false,
  };
}
